/**
 * The list-and-detail layout the Mash, Fermentation and Additions tabs share
 * (PLAN-PROFILES P3, PLAN-ADDITIONS-PROFILES).
 *
 * A list of saved profiles and whatever the selected one holds. The caller
 * says how to summarise a row in the list and what goes in the detail pane;
 * this owns selection, the phone sheet, and rename and delete.
 *
 * ## What these tabs are for
 *
 * Not editing. A profile is edited where it is used, on the recipe, and saved
 * from there. These tabs exist so a profile can be **read**, which is why the
 * detail pane leads with the description rather than with the table.
 *
 * Renaming and deleting are here because they have nowhere else to be, and
 * built-ins offer neither.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { NameDialog } from '@/components/name-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useMediaQuery } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';
import { useDispatch } from '@/store/use-dispatch';

/** What every saved-profile document carries, whichever library it is from. */
export interface LibraryProfile {
  readonly id: string;
  readonly name: string;
  readonly notes: string;
  readonly builtin: boolean;
}

export interface ProfileLibraryTabProps<TProfile extends LibraryProfile> {
  readonly profiles: readonly TProfile[];
  /** The command family to dispatch rename and delete through. */
  readonly domain: 'mashProfile' | 'fermentationProfile' | 'miscProfile';
  /** How a sentence names one of these, e.g. `mash schedule`. */
  readonly noun: string;
  /** What a recipe keeps after the profile changes, e.g. `steps`. */
  readonly contents: string;
  readonly emptyMessage: string;
  /** The line under a profile's name in the list, e.g. `4 steps`. */
  readonly summary: (profile: TProfile) => string;
  /** Everything in the detail pane below the name, notes and buttons. */
  readonly detail: (profile: TProfile) => ReactNode;
}

export function ProfileLibraryTab<TProfile extends LibraryProfile>({
  profiles,
  domain,
  noun,
  contents,
  emptyMessage,
  summary,
  detail: renderDetail,
}: ProfileLibraryTabProps<TProfile>) {
  const { dispatch } = useDispatch();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<TProfile | null>(null);
  const [renaming, setRenaming] = useState<TProfile | null>(null);
  /** `lg` — the width this tab's own grid switches to two columns at. */
  const wide = useMediaQuery('(min-width: 1024px)');

  const selected = profiles.find((p) => p.id === selectedId) ?? profiles[0];

  if (profiles.length === 0) {
    return <p className="px-1 py-6 text-muted-foreground text-sm">{emptyMessage}</p>;
  }

  const rename = (name: string) => {
    if (renaming === null || name === renaming.name) return;
    dispatch(`${domain}.update`, { profileId: renaming.id, changes: { name } });
  };

  /**
   * The selected profile's contents and prose, rendered in one of two places.
   *
   * At `lg` the tab is a master/detail and this is the detail column. Below
   * that the grid collapses to a single column, so selecting a profile used
   * to swap content *below the fold* — on a phone the tap looked like it did
   * nothing until you scrolled down and found the panel had changed. It goes
   * in a sheet there instead.
   */
  const detail =
    selected === undefined ? null : (
      <>
        <header className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-sm">{selected.name}</h3>
            {selected.notes !== '' && (
              <p className="mt-1 max-w-prose text-muted-foreground text-sm">{selected.notes}</p>
            )}
          </div>
          {!selected.builtin && (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" onClick={() => setRenaming(selected)}>
                Rename
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeleting(selected)}>
                Delete
              </Button>
            </div>
          )}
        </header>

        {renderDetail(selected)}
      </>
    );

  return (
    <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
      <ul className="flex flex-col gap-1">
        {profiles.map((profile) => (
          <li key={profile.id}>
            <button
              type="button"
              onClick={() => setSelectedId(profile.id)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left',
                profile.id === selected?.id
                  ? 'border-ring bg-accent/50'
                  : 'border-transparent hover:bg-accent/40',
              )}
            >
              <span className="flex items-center gap-2 font-medium text-sm">
                {profile.name}
                {/* `Built-in`, not `Yours`: Equipment and Water badge the
                    read-only ones, and a page whose four tabs disagreed about
                    what an unbadged row meant taught the convention on one tab
                    and broke it on the next. The badge marks the rows a brewer
                    cannot edit, which is the fact with a consequence. */}
                {profile.builtin && (
                  <Badge variant="muted" className="text-[10px]">
                    Built-in
                  </Badge>
                )}
              </span>
              <span className="text-muted-foreground text-xs">{summary(profile)}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* The detail column, at the width where there is room for one. */}
      {wide && detail !== null && <section className="flex flex-col gap-3">{detail}</section>}

      {/* And the same content as a sheet on a phone. `DialogContent` is
          already a bottom sheet with a pull-down below `sm`, so this needs no
          drawer of its own. Opening keys off `selectedId` rather than
          `selected`, which falls back to the first profile and would spring
          the sheet open on arrival. */}
      <Dialog
        open={!wide && selectedId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.name ?? ''}</DialogTitle>
          </DialogHeader>
          {detail}
        </DialogContent>
      </Dialog>

      <NameDialog
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null);
        }}
        title={`Rename this ${noun}`}
        description={`Recipes you have already applied it to keep their ${contents}, because applying one copies them in.`}
        initialValue={renaming?.name ?? ''}
        confirmLabel="Rename"
        onSubmit={rename}
      />
      {deleting !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title={`Delete ${deleting.name}?`}
          description={`We'll remove this ${noun}. Recipes already using it keep their ${contents}, because applying one copies them in.`}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            dispatch(`${domain}.delete`, { profileId: deleting.id });
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
